import React, { useEffect, useState } from 'react';
import api from '../services/api';
import IconButton from '../components/IconButton';
import DateInput from '../components/DateInput';
import SearchableSelect from '../components/SearchableSelect';

const formatINR = (value) =>
  Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function InvoiceRegister() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await api.get('/clients');
        const data = Array.isArray(res.data) ? res.data : res.data.data || [];
        setClients(data);
      } catch (e) {
        // ignore — client filter will be unavailable
      }
    };
    loadClients();
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {};
      if (clientFilter) params.client_id = clientFilter;
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      const res = await api.get('/invoices/register', { params });
      setInvoices(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => fetchInvoices();
  const resetFilters = () => {
    setClientFilter('');
    setFromDate('');
    setToDate('');
    setTimeout(fetchInvoices, 0);
  };

  const downloadInvoicePdf = async (invoice) => {
    try {
      const res = await api.get(`/invoices/${invoice.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoice_number || `invoice-${invoice.id}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.error || 'No PDF available for this invoice');
    }
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoice.invoice_number || invoice.id}? This will also remove its task associations.`)) {
      return;
    }
    try {
      setDeletingId(invoice.id);
      await api.delete(`/invoices/${invoice.id}`);
      setInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
      setSuccess(`Invoice ${invoice.invoice_number || invoice.id} deleted successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete invoice');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div>Loading invoices...</div>;

  return (
    <div className="clients-container">
      <h2>Invoice Register</h2>

      {error && <div style={{ background: '#FFEBE6', color: '#DE350B', padding: '12px 16px', borderRadius: 3, marginBottom: 20, textAlign: 'center' }}>{error}</div>}
      {success && <div style={{ background: '#E3FCEF', color: '#006644', padding: '12px 16px', borderRadius: 3, marginBottom: 20, textAlign: 'center' }}>{success}</div>}

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ minWidth: 200, width: 220 }}>
          <SearchableSelect
            label="Client"
            value={clientFilter}
            options={clients.map(c => ({ value: c.id, label: c.company || c.name || `Client ${c.id}` }))}
            onChange={e => setClientFilter(e.target.value)}
            placeholder="Select client"
            isClearable={true}
          />
        </div>
        <div style={{ minWidth: 160 }}>
          <DateInput label="From" value={fromDate} onChange={val => setFromDate(val)} />
        </div>
        <div style={{ minWidth: 160 }}>
          <DateInput label="To" value={toDate} onChange={val => setToDate(val)} />
        </div>
        <button className="generate-btn" onClick={applyFilters} style={{ height: 42 }}>Search</button>
        <button className="back-btn" onClick={resetFilters} style={{ height: 42 }}>Reset</button>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        {[
          { label: 'Total Taxable Amount', value: formatINR(invoices.reduce((s, inv) => s + Number(inv.taxable_amount || 0), 0)), bg: '#E3FCEF', border: '#ABF5D1', labelColor: '#006644', valueColor: '#006644' },
          { label: 'Total GST Amount', value: formatINR(invoices.reduce((s, inv) => s + Number(inv.gst_amount || 0), 0)), bg: '#FFF7E6', border: '#FFE0A3', labelColor: '#974F0C', valueColor: '#974F0C' },
          { label: 'Total Amount', value: formatINR(invoices.reduce((s, inv) => s + Number(inv.total_amount || 0), 0)), bg: '#E9F2FF', border: '#B3D4FF', labelColor: '#0747A6', valueColor: '#0747A6' }
        ].map(card => (
          <div key={card.label} style={{ flex: '1 1 200px', background: card.bg, border: `1px solid ${card.border}`, borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 13, color: card.labelColor, marginBottom: 6 }}>{card.label}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: card.valueColor }}>₹ {card.value}</div>
          </div>
        ))}
      </div>

      <div className="register-table-wrapper">
        <table className="clients-table register-table">
          <thead>
          <tr>
            <th>Invoice Id</th>
            <th>Invoice Date</th>
            <th>Client</th>
            <th>GSTIN</th>
            <th>Task Ids</th>
            <th>Taxable (₹)</th>
            <th>CGST (₹)</th>
            <th>SGST (₹)</th>
            <th>GST (₹)</th>
            <th>Total (₹)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(inv => (
            <tr key={inv.id}>
              <td>{inv.invoice_number}</td>
              <td>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : '-'}</td>
              <td>{inv.client?.company || inv.client?.name || inv.client_id || '-'}</td>
              <td>{inv.gstin || '-'}</td>
              <td>
                {inv.tasks && inv.tasks.length > 0 ? (
                  <ul style={{ margin: 0, paddingInlineStart: 16 }}>
                    {inv.tasks.map(t => (
                      <li key={t.task_id}>
                        <a href={`/tasks/edit/${t.task_id}`} target="_blank" rel="noopener noreferrer">{t.task_id}</a>
                        {typeof t.fee === 'number' && <span style={{ color: '#6B778C' }}> (₹{formatINR(t.fee)})</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  0
                )}
              </td>
              <td>{formatINR(inv.taxable_amount)}</td>
              <td>{formatINR(inv.cgst)}</td>
              <td>{formatINR(inv.sgst)}</td>
              <td>{formatINR(inv.gst_amount)}</td>
              <td>{formatINR(inv.total_amount)}</td>
              <td>
                <div className="action-buttons">
                  <IconButton title="View" onClick={() => alert(JSON.stringify(inv, null, 2))}>
                    <svg viewBox="0 0 24 24" fill="none"><path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </IconButton>
                  <IconButton title="Download PDF" onClick={() => downloadInvoicePdf(inv)}>
                    <svg viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4V4z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 2v4M12 2v4M16 2v4M10 14l2 2 2-2M12 4v10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </IconButton>
                  <IconButton title="Delete invoice" onClick={() => handleDelete(inv)} disabled={deletingId === inv.id}>
                    <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </IconButton>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <style>{`
        .register-table-wrapper {
          max-height: 70vh;
          overflow: auto;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
        }
        .register-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }
        .register-table thead th {
          position: sticky;
          top: 0;
          z-index: 10;
          background: #f0f1f3;
          box-shadow: inset 0 -1px 0 #e0e0e0;
          white-space: nowrap;
        }
        .register-table th,
        .register-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #eef0f2;
          font-size: 14px;
          text-align: left;
        }
        .register-table tbody tr:hover {
          background-color: rgba(0, 102, 204, 0.04);
        }
      `}</style>
    </div>
  );
}
